import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Check, Coins, Copy, ExternalLink, Gift, Wallet, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { copyToClipboard } from '@/lib/clipboard';
import {
  isPartialRedemption,
  loyaltyCustomerDashboardUrl,
  loyaltyRedemptionLabelKey,
  loyaltyRedemptionStatusKey,
  shortTraceId,
  type LoyaltyRedemptionKind,
  type LoyaltyRedemptionStatus,
  type LoyaltyRedemptionView,
} from '@/lib/loyalty-redemption';

const KIND_ICONS: Record<LoyaltyRedemptionKind, LucideIcon> = {
  balance: Wallet,
  coupon: Gift,
  other: Coins,
};

// Meaningful tints, so each carries its explicit dark pair; `released` is neutral on purpose.
const STATUS_CLASSES: Record<LoyaltyRedemptionStatus, string> = {
  held: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300',
  settled:
    'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-300',
  released: 'border-border bg-muted text-muted-foreground',
};

interface LoyaltyRedemptionRowProps {
  redemption: LoyaltyRedemptionView;
  /** Each surface prints money its own way (`MXN 10.00` on the order, `$10.00` in the cart). */
  formatMoney: (amount: number) => string;
  /**
   * `detail` — the order detail's totals block: coupon-row styling plus the trace (status,
   * transaction id, loyalty customer link). `compact` — a plain totals row (operator cart).
   */
  variant?: 'detail' | 'compact';
  /** Dashboard origin for the loyalty customer link; falsy hides the link. */
  dashboardUrl?: string | null;
}

/**
 * One totals line for loyalty money spent on an order — any redemption kind — rendered from the
 * normalised {@link LoyaltyRedemptionView}, so every surface names, signs and traces it the same way.
 */
export function LoyaltyRedemptionRow({
  redemption,
  formatMoney,
  variant = 'detail',
  dashboardUrl,
}: LoyaltyRedemptionRowProps) {
  const { t } = useTranslation('orders');
  const label = t(loyaltyRedemptionLabelKey(redemption.kind));
  const applied = redemption.appliedAmount;
  const partial = isPartialRedemption(redemption);
  const partialNote = partial ? t('loyalty.partial', { amount: formatMoney(redemption.heldAmount) }) : null;

  if (variant === 'compact') {
    return (
      <div className="flex justify-between gap-3 text-muted-foreground">
        <span className="min-w-0">
          {label}
          {partialNote && <span className="ml-1.5 text-xs">({partialNote})</span>}
        </span>
        <span className="shrink-0 tabular-nums">
          {applied > 0 ? `-${formatMoney(applied)}` : t('loyalty.noEffect')}
        </span>
      </div>
    );
  }

  const Icon = KIND_ICONS[redemption.kind];
  const customerHref =
    dashboardUrl && redemption.loyaltyCustomerId
      ? loyaltyCustomerDashboardUrl(dashboardUrl, redemption.loyaltyCustomerId)
      : null;

  return (
    <div className="space-y-1">
      <div className="flex justify-between gap-3 text-sm">
        <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5 text-muted-foreground">
          <Icon className="h-3.5 w-3.5 text-muted-foreground/70" aria-hidden="true" />
          <span className="font-medium text-foreground" title={redemption.rewardType}>
            {label}
          </span>
          {partialNote && <span className="text-xs text-muted-foreground">{partialNote}</span>}
          {!(applied > 0) && <span className="text-xs text-muted-foreground">{t('loyalty.noEffect')}</span>}
        </span>
        {applied > 0 && (
          <span className="shrink-0 font-medium text-emerald-700 dark:text-emerald-400 tabular-nums">
            -{formatMoney(applied)}
          </span>
        )}
      </div>
      <div className="ml-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span
          className={`inline-flex items-center rounded-full border px-1.5 py-px text-[11px] font-medium ${STATUS_CLASSES[redemption.status]}`}
          title={
            redemption.settledAt
              ? t('loyalty.settledAt', { date: format(new Date(redemption.settledAt), 'dd/MM/yyyy HH:mm') })
              : undefined
          }
        >
          {t(loyaltyRedemptionStatusKey(redemption.status))}
        </span>
        {redemption.transactionId ? (
          <TraceId label={t('loyalty.transaction')} id={redemption.transactionId} />
        ) : (
          redemption.reservationId && <TraceId label={t('loyalty.reservation')} id={redemption.reservationId} />
        )}
        {customerHref && (
          <a
            href={customerHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            {t('detail.viewLoyaltyCustomer')}
          </a>
        )}
      </div>
    </div>
  );
}

/** "Transacción 1a2b3c4d" with the full id copyable — what an operator pastes into the dashboard. */
function TraceId({ label, id }: { label: string; id: string }) {
  const { t } = useTranslation('orders');
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    const ok = await copyToClipboard(id);
    if (!ok) {
      toast.error(t('loyalty.copyFailed'));
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <span className="inline-flex items-center gap-1">
      {label}
      <code className="font-mono text-[11px] text-foreground" title={id}>
        {shortTraceId(id)}
      </code>
      <Button
        variant="ghost"
        size="icon"
        className="h-5 w-5"
        onClick={() => void copy()}
        aria-label={t('loyalty.copyId')}
        title={t('loyalty.copyId')}
      >
        {copied ? <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" /> : <Copy className="h-3 w-3" />}
      </Button>
    </span>
  );
}
